import { useEffect, useRef } from "react";

import type { CompanyListItem } from "../../api/companies";
import { CompanyRow } from "../../components/CompanyRow";
import { nl } from "../../i18n/nl";
import { formatAgo } from "../../lib/format";
import styles from "./companies.module.css";

const t = nl.companies;

function tagFor(company: CompanyListItem): string | undefined {
  if (company.is_my_claim) return t.tags.myClaim;
  if (company.on_waitlist) return t.tags.waitlist;
  if (company.followed) return t.tags.followed;
  return undefined;
}

function detailFor(company: CompanyListItem): string {
  const parts = [
    company.reason ? t.reasons[company.reason] : null,
    company.municipality,
    company.distance_km !== null ? t.distance(company.distance_km) : null,
  ];
  return parts.filter(Boolean).join(" · ");
}

export interface CompanyListProps {
  companies: CompanyListItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
}

/** Rows are buttons; ↑/↓ moves the selection (and focus) to the previous/next company. */
export function CompanyList({ companies, selectedId, onSelect }: CompanyListProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const latest = useRef({ companies, selectedId, onSelect });
  latest.current = { companies, selectedId, onSelect };

  // A native listener: the rows are the interactive elements, the list only routes ↑/↓.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const { companies: items, selectedId: selected, onSelect: select } = latest.current;
      const focused = (event.target as HTMLElement).closest<HTMLElement>("[data-company-id]");
      const current = items.findIndex((c) => c.id === (focused?.dataset.companyId ?? selected));
      const next = event.key === "ArrowDown" ? current + 1 : current - 1;
      const target = items[Math.max(0, Math.min(items.length - 1, next))];
      if (!target || !list) return;
      event.preventDefault();
      select(target.id);
      list.querySelector<HTMLElement>(`[data-company-id="${target.id}"] button`)?.focus();
    }
    list.addEventListener("keydown", onKeyDown);
    return () => {
      list.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <ul ref={listRef} className={styles.list} aria-label={t.listLabel}>
      {companies.map((company) => (
        <li key={company.id} data-company-id={company.id}>
          <CompanyRow
            company={{
              id: company.id,
              name: company.name,
              status: company.status,
              hue: company.avatar_hue,
              detail: detailFor(company),
              updated: company.last_update_at ? formatAgo(company.last_update_at) : undefined,
              tag: tagFor(company),
            }}
            selected={company.id === selectedId}
            onSelect={onSelect}
          />
        </li>
      ))}
    </ul>
  );
}
